import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { OrderDetails } from "../../../api/lib/db/types";

interface OrderFlowStore {
	selectedAddressId: number;
	orderDetails: OrderDetails | null;
	setSelectedAddressId: (id: number) => void;
	setOrderDetails: (details: OrderDetails | null) => void;
	clearOrderFlow: () => void;
}

interface AdminStore {
	showTodayOnly: boolean;
	setShowTodayOnly: (showTodayOnly: boolean) => void;
	sseConnected: boolean;
	setSSEConnected: (connected: boolean) => void;
}

interface CategoryStore {
	selectedCategory: string | null;
	setSelectedCategory: (category: string | null) => void;
}

export const useOrderFlowStore = create<OrderFlowStore>()(
	persist(
		(set) => ({
			selectedAddressId: 0,
			orderDetails: null,
			setSelectedAddressId: (id: number) => set({ selectedAddressId: id }),
			setOrderDetails: (details: OrderDetails | null) =>
				set({ orderDetails: details }),
			clearOrderFlow: () => set({ selectedAddressId: 0, orderDetails: null }),
		}),
		{ name: "order-flow-storage" },
	),
);

export const useAdminStore = create<AdminStore>()(
	persist(
		(set) => ({
			showTodayOnly: true,
			setShowTodayOnly: (showTodayOnly: boolean) => set({ showTodayOnly }),
			sseConnected: false,
			setSSEConnected: (connected: boolean) => set({ sseConnected: connected }),
		}),
		{ name: "admin-preferences" },
	),
);

export const useCategoryStore = create<CategoryStore>()(
	persist(
		(set) => ({
			selectedCategory: null,
			setSelectedCategory: (category: string | null) =>
				set({ selectedCategory: category }),
		}),
		{ name: "category-selection" },
	),
);

interface SplashState {
	isOpen: boolean;
	title: string;
	description?: string;
	variant: "default" | "celebration" | "error" | "alert";
	show: (options: {
		title: string;
		description?: string;
		variant?: "default" | "celebration" | "error" | "alert";
		duration?: number;
	}) => void;
	hide: () => void;
}

export const useSplashStore = create<SplashState>((set) => ({
	isOpen: false,
	title: "",
	description: "",
	variant: "default",
	show: ({ title, description, variant = "celebration", duration = 2000 }) => {
		set({ isOpen: true, title, description, variant });
		if (duration > 0) {
			setTimeout(() => {
				set({ isOpen: false });
			}, duration);
		}
	},
	hide: () => set({ isOpen: false }),
}));
